import { DiffEditor } from "@monaco-editor/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSaveFile, useSaveFrameParameter } from "../../api/hooks";
import type { ParameterDef } from "../../api/types";
import { EdgeResizer } from "../../components/Split";
import { Spinner, errText } from "../../components/ui";
import { languageFor } from "../../editor/CodeEditor";
import { useSession } from "../../app/store";
import { Markdown } from "./Markdown";
import {
  QUICK_PROMPTS,
  streamChat,
  useAssistantStatus,
  type ChatEvent,
  type FileProposal,
  type FrameParameterProposal,
  type Proposal,
  type ToolActivity,
} from "./assistantApi";
import { useChat, type ChatMessage, type TrackedProposal } from "./chatStore";
import "./assistant.css";

const TOOL_LABELS: Record<string, string> = {
  get_project_overview: "Reading project overview",
  search_parameters: "Searching parameters",
  list_files: "Listing files",
  read_file: "Reading file",
  list_flights: "Listing flights",
  parameter_stats: "Analysing parameter values",
  values_at: "Reading values",
  lint_python: "Linting",
  preview_derived: "Evaluating derived parameter",
  validate_report: "Validating report",
  preview_report: "Rendering report preview",
  run_triggers: "Running triggers on flight",
  propose_file: "Proposing file",
  propose_frame_parameter: "Proposing frame parameter",
};

const PAGE_FOR_KIND: Record<string, string> = {
  triggers: "/triggers",
  derived: "/derived",
  inputs: "/inputs",
  reports: "/reports",
  tests: "/tests",
  frames: "/frame",
  templates: "/reports",
};

export function AssistantPanel() {
  const { projectId, activeFile, activeFlight, selectedFlights, assistantWidth, setAssistantWidth, setAssistantOpen } = useSession();
  const location = useLocation();
  const status = useAssistantStatus();
  const chat = useChat();
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [includeFile, setIncludeFile] = useState(true);
  const [stick, setStick] = useState(true);

  useEffect(() => useChat.getState().ensureProject(projectId), [projectId]);

  // keep the transcript pinned to the bottom while streaming unless the user scrolled up
  useEffect(() => {
    const el = listRef.current;
    if (el && stick) el.scrollTop = el.scrollHeight;
  }, [chat.messages, stick]);
  // content can grow without a message change (a proposal's diff expanding, Monaco mounting) – keep following it
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (stick) el.scrollTop = el.scrollHeight;
    });
    for (const child of Array.from(el.children)) ro.observe(child);
    const mo = new MutationObserver(() => {
      ro.disconnect();
      for (const child of Array.from(el.children)) ro.observe(child);
    });
    mo.observe(el, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [stick]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
  };

  const send = useCallback(
    async (text: string) => {
      const msg = text.trim();
      if (!msg || !projectId || chat.busy) return;
      const state = useChat.getState();
      const history = state.messages.filter((m) => !m.streaming && m.text).map((m) => ({ role: m.role, content: m.text }));
      const id = state.beginTurn(msg);
      state.setDraft("");
      setStick(true);
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const st = useChat.getState();
      const onEvent = (e: ChatEvent) => {
        switch (e.event) {
          case "text":
            st.appendText(id, e.data.delta);
            break;
          case "tool_start":
            st.addTool(id, { id: e.data.id, name: e.data.name, input: e.data.input, done: false });
            break;
          case "tool_end":
            st.endTool(id, e.data.id, e.data.ok, e.data.summary);
            break;
          case "proposal":
            st.addProposal(id, e.data);
            break;
          case "notice":
            st.addNotice(id, e.data.message);
            break;
          case "error":
            st.failTurn(id, e.data.message);
            break;
          case "start":
            useChat.setState({ conversationId: e.data.conversation_id, model: e.data.model });
            break;
          case "done":
            st.endTurn(id, e.data.conversation_id);
            break;
        }
      };
      try {
        await streamChat(
          {
            project: projectId,
            message: msg,
            conversation_id: state.conversationId,
            history,
            applied: state.takeDecisions(),
            context: {
              route: location.pathname,
              active_file: includeFile ? activeFile : null,
              active_flight: activeFlight,
              selected_flights: selectedFlights,
            },
          },
          onEvent,
          ctrl.signal,
        );
        const m = useChat.getState().messages.find((x) => x.id === id);
        if (m?.streaming) st.endTurn(id, null);
      } catch (err) {
        if ((err as Error).name === "AbortError") st.failTurn(id, "stopped");
        else st.failTurn(id, errText(err));
      } finally {
        abortRef.current = null;
      }
    },
    [projectId, chat.busy, location.pathname, includeFile, activeFile, activeFlight, selectedFlights],
  );

  const stop = () => abortRef.current?.abort();

  const newChat = () => {
    stop();
    chat.reset();
  };

  const contextChips = (
    <div className="ai-context">
      <span className="ai-chip" title="Current page">
        {location.pathname.replace("/", "") || "home"}
      </span>
      {activeFile ? (
        <button
          className={`ai-chip toggle${includeFile ? " on" : ""}`}
          onClick={() => setIncludeFile((v) => !v)}
          title={includeFile ? "The open file is sent as context (click to exclude)" : "Open file excluded from context (click to include)"}
        >
          {includeFile ? "☑" : "☐"} {activeFile.split("/").pop()}
        </button>
      ) : null}
      {activeFlight ? (
        <span className="ai-chip" title="Active flight (used by analysis tools)">
          ✈ {activeFlight}
        </span>
      ) : null}
    </div>
  );

  return (
    <aside className="assistant" style={{ width: assistantWidth }}>
      <EdgeResizer
        axis="x"
        invert
        className="assistant-resizer"
        value={assistantWidth}
        onChange={setAssistantWidth}
        onReset={() => setAssistantWidth(400)}
        title="Drag to resize assistant · double-click to reset · Ctrl+I to hide"
      />
      <div className="assistant-head">
        <span className="ai-title">✦ Assistant</span>
        {status.data?.enabled ? <span className="badge ok mono">{chat.model ?? status.data.model}</span> : null}
        <span className="spacer" />
        <button className="btn ghost sm" onClick={newChat} title="New conversation" disabled={!chat.messages.length}>
          ⟲
        </button>
        <button className="btn ghost sm" onClick={() => setAssistantOpen(false)} title="Hide (Ctrl+I)">
          ✕
        </button>
      </div>

      {status.isLoading ? (
        <div className="assistant-body">
          <Spinner label="checking assistant…" />
        </div>
      ) : status.data && !status.data.enabled ? (
        <SetupHelp reason={status.data.reason} />
      ) : !projectId ? (
        <div className="assistant-body">
          <div className="empty">
            <h3>No project</h3>Select or create a project to start.
          </div>
        </div>
      ) : (
        <>
          <div className="assistant-body" ref={listRef} onScroll={onScroll}>
            {chat.messages.length === 0 ? (
              <Welcome onPick={(p) => chat.setDraft(p)} hasFile={!!activeFile} hasFlight={!!activeFlight} />
            ) : (
              chat.messages.map((m) => <MessageView key={m.id} m={m} projectId={projectId} onFollowUp={send} />)
            )}
          </div>
          <div className="assistant-foot">
            {contextChips}
            <Composer
              value={chat.draft}
              onChange={chat.setDraft}
              busy={chat.busy}
              onSend={() => void send(chat.draft)}
              onStop={stop}
            />
            <div className="small faint ai-hint">
              Enter to send · Shift+Enter for a new line · proposals are written only when you press Apply
              {chat.decisions.length ? ` · ${chat.decisions.length} decision(s) will be sent with your next message` : ""}
            </div>
          </div>
        </>
      )}
      {status.isError ? <div className="assistant-body small dim">Assistant unavailable: {errText(status.error)}</div> : null}
    </aside>
  );
}

function SetupHelp({ reason }: { reason: string }) {
  return (
    <div className="assistant-body">
      <div className="card ai-setup">
        <h4>Assistant not configured</h4>
        <p className="small dim">{reason}</p>
        <p className="small">Set one of these on the backend and restart it:</p>
        <pre className="out">
          {`# Anthropic (default model claude-sonnet-5)
ANTHROPIC_API_KEY=sk-ant-…

# OpenAI
OPENAI_API_KEY=sk-…
REPORTS_STUDIO_AI_MODEL=gpt-5

# any OpenAI-compatible server (Ollama, vLLM, Azure, LM Studio)
REPORTS_STUDIO_AI_PROVIDER=openai
REPORTS_STUDIO_AI_BASE_URL=http://localhost:11434/v1
REPORTS_STUDIO_AI_MODEL=qwen2.5-coder:32b

# optional
REPORTS_STUDIO_AI_MAX_TOKENS=8192
REPORTS_STUDIO_AI_MAX_STEPS=16
REPORTS_STUDIO_AI_PROVIDER=off   # hide the assistant`}
        </pre>
        <p className="small dim">
          The assistant reads the project, flights and runs through the same API you use; it only writes files when you apply a proposal.
        </p>
      </div>
    </div>
  );
}

function Welcome({ onPick, hasFile, hasFlight }: { onPick: (p: string) => void; hasFile: boolean; hasFlight: boolean }) {
  return (
    <div className="ai-welcome">
      <h3>What do you want to build?</h3>
      <p className="dim small">
        I can write triggers, derived parameters, inputs and report definitions for this project, check them against your frame and flights,
        and hand them to you as reviewable proposals.
      </p>
      <div className="ai-quick">
        {QUICK_PROMPTS.map((q) => (
          <button key={q.label} className="btn sm" onClick={() => onPick(q.prompt)}>
            {q.label}
          </button>
        ))}
      </div>
      <ul className="small dim ai-tips">
        <li>{hasFile ? "The file open in the editor is sent along as context." : "Open a file in an editor page to give me context."}</li>
        <li>{hasFlight ? "Analysis tools use your active flight." : "Select a flight on the Flights page so I can look at real values."}</li>
        <li>Describe conditions in plain language; I will map them to mnemonics in your frame.</li>
      </ul>
    </div>
  );
}

function Composer({
  value,
  onChange,
  busy,
  onSend,
  onStop,
}: {
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  onSend: () => void;
  onStop: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = Math.min(220, Math.max(44, el.scrollHeight)) + "px";
  }, [value]);
  return (
    <div className="ai-composer">
      <textarea
        ref={ref}
        className="inp"
        placeholder="Ask for a trigger, a derived parameter, a report… (Enter to send)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            if (!busy) onSend();
          }
        }}
      />
      {busy ? (
        <button className="btn danger sm" onClick={onStop} title="Stop generating">
          ■ Stop
        </button>
      ) : (
        <button className="btn primary sm" onClick={onSend} disabled={!value.trim()} title="Send (Enter)">
          Send
        </button>
      )}
    </div>
  );
}

function MessageView({ m, projectId, onFollowUp }: { m: ChatMessage; projectId: string; onFollowUp: (t: string) => void }) {
  const proposals = useChat((s) => s.proposals);
  if (m.role === "user") {
    return (
      <div className="ai-msg user">
        <div className="ai-bubble">{m.text}</div>
      </div>
    );
  }
  const mine = m.proposalIds.map((id) => proposals[id]).filter(Boolean) as TrackedProposal[];
  return (
    <div className="ai-msg assistant">
      {m.tools.length ? (
        <div className="ai-tools">
          {m.tools.map((t) => (
            <ToolChip key={t.id} t={t} />
          ))}
        </div>
      ) : null}
      {m.text ? <Markdown text={m.text} /> : m.streaming && !m.tools.length ? <Spinner label="thinking…" /> : null}
      {m.streaming && m.text ? <span className="ai-cursor" /> : null}
      {mine.map((tp) => (
        <ProposalCard key={tp.proposal.id} tp={tp} projectId={projectId} onFollowUp={onFollowUp} />
      ))}
      {m.notices.map((n, i) => (
        <div key={i} className="ai-notice small">
          {n}
        </div>
      ))}
      {m.error ? <div className="ai-error small">{m.error}</div> : null}
    </div>
  );
}

function ToolChip({ t }: { t: ToolActivity }) {
  const [open, setOpen] = useState(false);
  const label = TOOL_LABELS[t.name] ?? t.name;
  const arg = useMemo(() => {
    const i = t.input ?? {};
    if (typeof i.query === "string") return `“${i.query}”`;
    if (typeof i.path === "string") return i.path;
    if (typeof i.mnemonic === "string") return i.mnemonic;
    if (Array.isArray(i.mnemonics)) return (i.mnemonics as string[]).slice(0, 4).join(", ") + ((i.mnemonics as string[]).length > 4 ? "…" : "");
    if (typeof i.flight_id === "string") return i.flight_id;
    return "";
  }, [t.input]);
  return (
    <div className={`ai-tool${t.done ? (t.ok ? " ok" : " err") : " busy"}`} onClick={() => setOpen((v) => !v)} title="Click for details">
      <span className="ai-tool-ico">{t.done ? (t.ok ? "✓" : "!") : <span className="spinner tiny" />}</span>
      <span className="ai-tool-label">{label}</span>
      {arg ? <span className="ai-tool-arg mono">{arg}</span> : null}
      {t.done && t.summary ? <span className="ai-tool-sum dim">· {t.summary}</span> : null}
      {open ? <pre className="ai-tool-detail">{JSON.stringify(t.input, null, 1)}</pre> : null}
    </div>
  );
}

function ProposalCard({ tp, projectId, onFollowUp }: { tp: TrackedProposal; projectId: string; onFollowUp: (t: string) => void }) {
  const p = tp.proposal;
  const [showDiff, setShowDiff] = useState(false);
  const setStatus = useChat((s) => s.setProposalStatus);
  const saveFile = useSaveFile(projectId);
  const saveParam = useSaveFrameParameter(projectId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { setOpenTriggerFile, setPendingOpenFile, toast } = useSession();
  const theme = useSession((s) => s.theme);
  const errors = p.problems.filter((x) => x.severity === "error");
  const warnings = p.problems.filter((x) => x.severity !== "error");
  const cardRef = useRef<HTMLDivElement>(null);
  // bring the whole card (head + expanded diff) into view when the diff opens
  useEffect(() => {
    if (!showDiff) return;
    const t = window.setTimeout(() => cardRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 120);
    return () => window.clearTimeout(t);
  }, [showDiff]);

  const apply = async () => {
    setStatus(p.id, "applying");
    try {
      if (p.kind === "file") {
        const r = await saveFile.mutateAsync({ path: p.path, content: p.content });
        if (p.path.startsWith("tests/")) qc.invalidateQueries({ queryKey: ["tests", projectId] });
        qc.invalidateQueries({ queryKey: ["versions", projectId] });
        const errs = (r.problems ?? []).filter((x) => x.severity === "error");
        toast(errs.length ? `${p.path} saved with ${errs.length} problem(s)` : `${p.path} saved`, errs.length ? "error" : "success");
      } else {
        await saveParam.mutateAsync({ frame: p.frame, mnemonic: p.mnemonic, parameter: p.parameter as Partial<ParameterDef> });
        toast(`${p.mnemonic} saved to ${p.frame}`, "success");
      }
      setStatus(p.id, "applied");
    } catch (err) {
      setStatus(p.id, "failed", errText(err));
    }
  };

  const open = () => {
    if (p.kind === "frame_parameter") {
      navigate("/frame");
      return;
    }
    const page = PAGE_FOR_KIND[p.file_kind];
    if (p.file_kind === "triggers") setOpenTriggerFile(p.path);
    else setPendingOpenFile(p.path);
    if (page) navigate(page);
  };

  const title = p.kind === "file" ? p.path : `${p.mnemonic} → ${p.frame}`;
  const verb = p.exists ? "update" : "create";
  return (
    <div className={`ai-proposal ${tp.status}`} ref={cardRef}>
      <div className="ai-proposal-head">
        <span className={`badge ${p.exists ? "warn" : "ok"}`}>{p.kind === "file" ? verb : p.exists ? "replace" : "add"}</span>
        <span className="mono ai-proposal-path" title={title}>
          {title}
        </span>
        <span className="spacer" />
        {errors.length ? <span className="badge err">{errors.length} error{errors.length > 1 ? "s" : ""}</span> : null}
        {warnings.length ? <span className="badge warn">{warnings.length} warning{warnings.length > 1 ? "s" : ""}</span> : null}
        {tp.status === "applied" ? <span className="badge ok">applied</span> : null}
        {tp.status === "rejected" ? <span className="badge">rejected</span> : null}
        {tp.status === "failed" ? <span className="badge err">failed</span> : null}
      </div>
      {p.summary ? <div className="ai-proposal-sum small">{p.summary}</div> : null}
      {p.problems.length ? (
        <ul className="ai-problems small">
          {p.problems.slice(0, 6).map((pr, i) => (
            <li key={i} className={`sev-${pr.severity}`}>
              {pr.line ? <span className="mono dim">L{pr.line} </span> : null}
              {pr.message}
            </li>
          ))}
          {p.problems.length > 6 ? <li className="dim">… {p.problems.length - 6} more</li> : null}
        </ul>
      ) : null}
      {tp.error ? <div className="ai-error small">{tp.error}</div> : null}
      <div className="ai-proposal-actions">
        <button className="btn sm" onClick={() => setShowDiff((v) => !v)}>
          {showDiff ? "Hide" : p.kind === "file" && p.exists ? "Diff" : "View"}
        </button>
        {tp.status === "pending" || tp.status === "failed" ? (
          <>
            <button className="btn primary sm" onClick={() => void apply()} disabled={saveFile.isPending || saveParam.isPending}>
              {tp.status === "failed" ? "Retry apply" : "Apply"}
            </button>
            <button className="btn ghost sm" onClick={() => setStatus(p.id, "rejected")}>
              Reject
            </button>
          </>
        ) : tp.status === "applying" ? (
          <Spinner label="saving…" />
        ) : tp.status === "applied" ? (
          <button className="btn sm" onClick={open}>
            Open
          </button>
        ) : null}
        {tp.status === "pending" && errors.length ? (
          <button className="btn ghost sm" onClick={() => onFollowUp(`Fix the ${errors.length} lint error(s) in your proposal for ${title} and propose it again.`)}>
            Ask to fix
          </button>
        ) : null}
      </div>
      {showDiff ? (
        p.kind === "file" ? (
          <FileDiff p={p} theme={theme} />
        ) : (
          <ParamView p={p as FrameParameterProposal} />
        )
      ) : null}
    </div>
  );
}

function FileDiff({ p, theme }: { p: FileProposal; theme: "dark" | "light" }) {
  const lines = Math.max(p.content.split("\n").length, (p.original ?? "").split("\n").length);
  const height = Math.min(520, Math.max(160, lines * 19 + 24));
  const editorRef = useRef<import("monaco-editor").editor.IStandaloneDiffEditor | null>(null);
  // detach the models before the widget is disposed, then dispose them ourselves
  // (the wrapper's own order triggers "TextModel got disposed before DiffEditorWidget model got reset")
  useEffect(
    () => () => {
      const ed = editorRef.current;
      if (!ed) return;
      try {
        const m = ed.getModel();
        ed.setModel(null);
        m?.original.dispose();
        m?.modified.dispose();
      } catch {
        /* already disposed */
      }
    },
    [],
  );
  return (
    <div className="ai-diff" style={{ height }}>
      <DiffEditor
        original={p.original ?? ""}
        modified={p.content}
        language={languageFor(p.path)}
        theme={theme === "dark" ? "rs-dark" : "rs-light"}
        keepCurrentOriginalModel
        keepCurrentModifiedModel
        onMount={(ed) => {
          editorRef.current = ed;
        }}
        options={{
          readOnly: true,
          originalEditable: false,
          renderSideBySide: false,
          minimap: { enabled: false },
          fontSize: 12,
          lineNumbersMinChars: 3,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          ignoreTrimWhitespace: false,
          renderOverviewRuler: false,
          hideUnchangedRegions: { enabled: !!p.original },
        }}
      />
    </div>
  );
}

function ParamView({ p }: { p: FrameParameterProposal }) {
  return (
    <div className="ai-param">
      {p.original ? (
        <div>
          <div className="small dim">current</div>
          <pre className="out">{JSON.stringify(p.original, null, 1)}</pre>
        </div>
      ) : null}
      <div>
        <div className="small dim">proposed</div>
        <pre className="out">{JSON.stringify(p.parameter, null, 1)}</pre>
      </div>
    </div>
  );
}

export type { Proposal };
